/**
 * StreetDiceShell — STATIC visual blueprint for the Street Dice redesign.
 *
 * Presentation ONLY, hard-coded placeholder data. NO circle game-board, NO giant
 * "B" marker, NO large point card over the dice, NO oversized side-bets panel.
 * Dice land on a physical street pit; players float around the perimeter.
 * Route: /casino/street-dice/preview-shell (preview only).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Crown, Dices, Bell, Menu, Sparkles, Eraser, RotateCcw, Ban, X,
} from 'lucide-react';

const GOLD = '#FFD700';

/* ---- a single realistic die face (large) ---- */
const PIPS = {
    1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8],
    5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8],
};
const Die = ({ value = 5, size = 140, tilt = 0 }) => (
    <div
        className="grid grid-cols-3 grid-rows-3 p-3"
        style={{
            width: size, height: size, transform: `rotate(${tilt}deg)`,
            borderRadius: size * 0.18,
            background: 'linear-gradient(145deg,#ffffff 0%,#ece6d8 100%)',
            boxShadow: '0 14px 26px rgba(0,0,0,0.75), inset 0 -6px 10px rgba(0,0,0,0.2), inset 0 4px 7px rgba(255,255,255,0.95)',
            border: '1px solid rgba(0,0,0,0.12)',
        }}
    >
        {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="flex items-center justify-center">
                {PIPS[value].includes(i) && (
                    <span className="rounded-full" style={{ width: size * 0.17, height: size * 0.17, background: 'radial-gradient(circle at 35% 30%,#5a0d0d,#9b0000)' }} />
                )}
            </div>
        ))}
    </div>
);

/* ---- premium green cash-bundle denomination button ---- */
const CashStack = ({ amount, active }) => (
    <button type="button" className={`group relative flex flex-col items-center gap-1 px-1 pt-2 pb-1 rounded-xl active:scale-95 transition ${active ? 'bg-[#FFD700]/10' : ''}`} data-testid={`shell-denom-${amount}`}>
        <span className="relative block w-12 h-9 group-hover:-translate-y-1 transition-transform">
            {[0, 5, 10, 15].map((b, idx) => (
                <span key={b} className="absolute rounded-[3px] border flex items-center justify-center"
                    style={{
                        left: 2 + idx * 0.5, right: 2 + idx * 0.5, bottom: b, height: 9,
                        background: `linear-gradient(to bottom, hsl(142 42% ${44 + idx * 4}%), hsl(142 46% ${26 + idx * 3}%))`,
                        borderColor: '#123a21',
                        boxShadow: idx === 3 ? 'inset 0 1px 2px rgba(255,255,255,0.3)' : '0 1px 2px rgba(0,0,0,0.5)',
                    }}>
                    {idx === 3 && <span className="text-[8px] font-black text-[#0c3b1d]">$</span>}
                </span>
            ))}
            <span className="absolute left-3.5 right-3.5 bottom-[16px] h-1.5 rounded-sm" style={{ background: 'linear-gradient(90deg,#FFF2B0,#FFD700,#C98A1A)', boxShadow: '0 0 6px rgba(255,215,0,0.8)' }} />
        </span>
        <span className="text-sm font-black text-[#FFD700] drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">${amount}</span>
    </button>
);

/* ---- floating compact black/gold player CARD (no circular icon) ---- */
const PlayerChip = ({ name, role, stack, shooter, fader, you, style }) => (
    <div className="absolute z-20 rounded-lg border px-2 py-1.5 flex items-center gap-2 backdrop-blur-sm" style={{
        ...style,
        background: shooter ? 'linear-gradient(135deg,rgba(255,215,0,0.9),rgba(201,138,26,0.85))' : 'rgba(8,6,3,0.82)',
        borderColor: shooter ? '#FFD700' : fader ? '#FF3B30' : 'rgba(255,215,0,0.3)',
        boxShadow: shooter ? '0 0 20px rgba(255,215,0,0.8)' : fader ? '0 0 12px rgba(255,59,48,0.5)' : '0 3px 12px rgba(0,0,0,0.75)',
    }}>
        <div className="w-7 h-7 rounded-md flex items-center justify-center font-black text-xs shrink-0"
            style={{ background: shooter ? 'rgba(0,0,0,0.85)' : 'linear-gradient(135deg,#FFE680,#C98A1A)', color: shooter ? '#FFD700' : '#000' }}>
            {name.charAt(0)}
        </div>
        <div className="leading-tight min-w-0">
            <div className="text-[11px] font-black truncate" style={{ color: shooter ? '#1a1205' : '#fff' }}>{name}{you ? ' · YOU' : ''}</div>
            {role
                ? <div className="text-[8px] font-black tracking-widest" style={{ color: shooter ? '#1a1205' : '#FF3B30' }}>{role}</div>
                : <div className="text-[10px] font-mono font-bold" style={{ color: shooter ? '#1a1205' : '#FFD700' }}>${stack}</div>}
        </div>
    </div>
);

const QUICK = ['Nice roll!', 'Come on 7!', 'Pay that man', 'Fade you!', 'Seven out'];

export default function StreetDiceShell() {
    const navigate = useNavigate();
    const [sheet, setSheet] = useState(false);

    return (
        <div className="min-h-screen w-full relative" style={{ background: '#050300' }}>
            <div className="relative mx-auto max-w-md">

                {/* ================= STREET ENVIRONMENT + PHYSICAL DICE PIT ================= */}
                <div className="relative w-full overflow-hidden" style={{ aspectRatio: '1 / 1' }} data-testid="shell-stage">
                    <div className="absolute inset-0"
                        style={{
                            backgroundImage: 'url(/street-dice/pit-scene.jpg)', backgroundSize: 'cover', backgroundPosition: 'center 28%',
                            filter: 'brightness(1.24) contrast(1.2) saturate(1.3)',
                        }} />
                    <div className="absolute inset-0 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 60%, rgba(5,3,0,0.97) 100%)' }} />

                    {/* top bar */}
                    <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-3 pt-3">
                        <button onClick={() => navigate('/lobby')}>
                            <img src="/street-dice/betohalics-logo.png" alt="Betohalics" className="h-8 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]" draggable={false} />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="px-3 py-1 rounded-full bg-black/65 border border-[#FFD700]/45 backdrop-blur-sm">
                                <div className="text-[8px] font-bold tracking-widest text-white/55 leading-none">BALANCE</div>
                                <div className="text-sm font-black text-[#FFD700] leading-tight">$1,000</div>
                            </div>
                            <button className="w-8 h-8 rounded-full bg-black/65 border border-white/15 flex items-center justify-center text-white/80"><Bell className="w-4 h-4" /></button>
                            <button className="w-8 h-8 rounded-full bg-black/65 border border-white/15 flex items-center justify-center text-white/80"><Menu className="w-4 h-4" /></button>
                        </div>
                    </div>

                    {/* STREET DICE identity */}
                    <div className="absolute top-[42px] inset-x-0 z-20 flex items-center justify-center gap-2 select-none">
                        <Dices className="w-6 h-6 text-[#FFD700] rotate-[-12deg] drop-shadow-[0_0_10px_rgba(255,215,0,0.8)]" />
                        <h1 className="text-4xl font-black" style={{ fontFamily: '"Bebas Neue","Impact",sans-serif', letterSpacing: '0.03em', background: 'linear-gradient(180deg,#FFF6C2,#FFD700 48%,#B8781A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.9))' }}>STREET DICE</h1>
                        <Dices className="w-6 h-6 text-[#FFD700] rotate-[12deg] drop-shadow-[0_0_10px_rgba(255,215,0,0.8)]" />
                    </div>

                    {/* small POINT badge (does not cover dice) */}
                    <div className="absolute top-[42px] right-3 z-30 px-2.5 py-1 rounded-full border border-[#FFD700]/60 bg-black/70 backdrop-blur-sm" data-testid="shell-point">
                        <span className="text-[9px] font-bold tracking-widest text-white/55">POINT </span>
                        <span className="text-sm font-black text-[#FFD700]">6</span>
                    </div>

                    {/* floating players AROUND the perimeter */}
                    <PlayerChip name="Vic" stack={200} style={{ left: '6%', top: '20%' }} />
                    <PlayerChip name="Big Mo" fader role="FADER" style={{ right: '6%', top: '20%' }} />
                    <PlayerChip name="Smooth" stack={200} style={{ left: '4%', top: '55%' }} />

                    {/* ===== REALISTIC PHYSICAL DICE PIT (throwing surface — NO circle) ===== */}
                    <div className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 z-10 w-[82%]" data-testid="shell-pit">
                        <div className="relative rounded-[26px] overflow-hidden"
                            style={{
                                aspectRatio: '16 / 10',
                                background: 'radial-gradient(ellipse at 50% 36%, #1a1309 0%, #0b0803 62%, #050300 100%)',
                                border: '1px solid rgba(255,215,0,0.22)',
                                boxShadow: 'inset 0 8px 32px rgba(0,0,0,0.92), inset 0 -5px 22px rgba(0,0,0,0.85), 0 20px 46px rgba(0,0,0,0.85)',
                            }}>
                            {/* gritty asphalt speckle */}
                            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)', backgroundSize: '7px 7px' }} />
                            {/* gold Betohalics crown marking painted in the pit center */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Crown className="w-28 h-28 text-[#FFD700] opacity-[0.15]" fill="#FFD700" />
                            </div>
                            {/* the two LARGE dice physically inside the pit */}
                            <div className="absolute inset-0 flex items-end justify-center gap-5 pb-4">
                                <Die value={4} tilt={-11} />
                                <Die value={3} tilt={10} />
                            </div>
                        </div>
                    </div>

                    {/* shooter card — centered near the pit (identifies shooter; no giant B) */}
                    <PlayerChip name="Blake1" role="SHOOTER" shooter you style={{ left: '50%', bottom: '2%', transform: 'translateX(-50%)' }} />
                </div>

                {/* ================= LOWER CONTROLS ================= */}
                <div className="relative -mt-2 px-3 pb-6" style={{ background: 'linear-gradient(180deg,#050300,#0a0803)' }}>
                    {/* compact reactions + chat */}
                    <div className="pt-2">
                        <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                            {QUICK.map((q) => <span key={q} className="shrink-0 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/75 font-semibold">{q}</span>)}
                        </div>
                        <div className="mt-1.5 flex gap-2">
                            <input placeholder="Say something to the corner…" className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 h-8 text-sm text-white placeholder:text-white/35 focus:outline-none" />
                            <button className="px-4 h-8 rounded-lg font-black text-black text-xs tracking-widest" style={{ background: GOLD }}>SEND</button>
                        </div>
                    </div>

                    {/* SHOOTER | FADER (short) */}
                    <div className="mt-2.5 grid grid-cols-2 gap-2">
                        <div className="h-11 rounded-xl border-2 px-3 flex items-center gap-2" style={{ borderColor: GOLD, background: 'linear-gradient(135deg,rgba(255,215,0,0.24),rgba(255,215,0,0.05))', boxShadow: '0 0 16px rgba(255,215,0,0.4)' }}>
                            <Crown className="w-5 h-5 text-[#FFD700]" fill={GOLD} />
                            <span className="text-xl font-black italic text-[#FFD700]" style={{ fontFamily: '"Bebas Neue","Impact",sans-serif' }}>SHOOTER</span>
                            <span className="ml-auto text-[9px] font-bold tracking-widest text-white/55">7 · 11</span>
                        </div>
                        <div className="h-11 rounded-xl border-2 px-3 flex items-center gap-2" style={{ borderColor: 'rgba(255,59,48,0.4)', background: 'rgba(255,59,48,0.06)' }}>
                            <Ban className="w-5 h-5 text-[#FF3B30]" />
                            <span className="text-xl font-black italic text-[#FF3B30]" style={{ fontFamily: '"Bebas Neue","Impact",sans-serif' }}>FADER</span>
                            <span className="ml-auto text-[9px] font-bold tracking-widest text-white/55">2 · 3 · 12</span>
                        </div>
                    </div>

                    {/* WAGER label + ONE cash row + small SIDE BETS button */}
                    <div className="mt-2.5 flex items-center justify-between px-0.5 mb-1">
                        <span className="text-[10px] font-black tracking-widest text-white/55">WAGER</span>
                        <span className="text-sm font-black text-[#FFD700]">$10</span>
                    </div>
                    <div className="flex items-end gap-2">
                        <div className="flex-1 flex items-end justify-between gap-1">
                            {[1, 5, 10, 25, 100].map((d) => <CashStack key={d} amount={d} active={d === 10} />)}
                        </div>
                        <button onClick={() => setSheet(true)} className="shrink-0 flex flex-col items-center gap-0.5 rounded-xl border px-3 py-2 text-[#FFD700]"
                            style={{ borderColor: 'rgba(255,215,0,0.6)', background: 'linear-gradient(135deg,rgba(255,215,0,0.14),rgba(0,0,0,0.5))' }} data-testid="shell-side-bets">
                            <Sparkles className="w-4 h-4" />
                            <span className="text-[8px] font-black tracking-widest">SIDE BETS</span>
                        </button>
                    </div>

                    {/* CLEAR | ROLL DICE | REPEAT */}
                    <div className="mt-3 grid grid-cols-[auto_1fr_auto] gap-2 items-stretch">
                        <button className="rounded-xl border border-white/20 bg-white/5 px-4 flex flex-col items-center justify-center text-white/80">
                            <Eraser className="w-5 h-5" /><span className="text-[9px] font-black tracking-widest mt-0.5">CLEAR</span>
                        </button>
                        <button className="rounded-xl h-16 flex items-center justify-center gap-2 font-black text-black text-2xl tracking-wide"
                            style={{ background: 'linear-gradient(180deg,#FFE680,#FFD700 50%,#D99A14)', fontFamily: '"Bebas Neue","Impact",sans-serif', boxShadow: '0 0 28px rgba(255,215,0,0.65)' }} data-testid="shell-roll">
                            <Dices className="w-7 h-7" /> ROLL DICE
                        </button>
                        <button className="rounded-xl border border-white/20 bg-white/5 px-4 flex flex-col items-center justify-center text-white/80">
                            <RotateCcw className="w-5 h-5" /><span className="text-[9px] font-black tracking-widest mt-0.5">REPEAT</span>
                        </button>
                    </div>

                    <div className="mt-3 text-center"><span className="text-[10px] text-white/30 font-mono">STATIC PREVIEW · visual blueprint · no actions wired yet</span></div>
                </div>
            </div>

            {/* ===== SIDE BETS bottom sheet (only when opened) ===== */}
            {sheet && (
                <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="shell-side-bets-sheet">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setSheet(false)} />
                    <div className="relative w-full max-w-md rounded-t-3xl border-t-2 border-[#FFD700]/50 bg-[#0c0a05] p-4 pb-8" style={{ boxShadow: '0 -10px 40px rgba(0,0,0,0.8)' }}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FFD700]" /><span className="text-lg font-black text-white" style={{ fontFamily: '"Bebas Neue","Impact",sans-serif' }}>SIDE BETS</span></div>
                            <button onClick={() => setSheet(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-xs text-white/60 mb-3">Shooter offers side action to the corner. Opponents tap to accept.</p>
                        <div className="grid grid-cols-2 gap-2">
                            {['Hard 6', 'Any 7', 'Craps 2/3/12', 'Boxcars 12'].map((s) => (
                                <div key={s} className="rounded-xl border border-white/12 bg-white/5 p-3">
                                    <div className="text-sm font-black text-white">{s}</div>
                                    <div className="text-[10px] text-white/45">tap to offer</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
